import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { withStyles, createStyles, WithStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import RestaurantIcon from './RestaurantIcons';
import RestaurantSchema from './RestaurantSchema';
import ArrowTop from '../../Common/Icons/ArrowTop';
import ArrowBottom from '../../Common/Icons/ArrowBottom';
import { ITable } from '../../../types/IRestaurantTablesStore';
import { TActiveTables } from '../../../types/IReserveModalStore';
import { TSectionAdaptSchema } from '../../../types/IRestaurantTablesSchemaStore';
import { IViewType } from '../ReservesDataFields';
import locales from '../../../locales';

const styles = (theme: Theme) => createStyles({
  wrap: {
    padding: 20,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    height: '100%', // Используем 100% от родительского контейнера вместо calc(100vh)
    overflow: 'auto',
    minHeight: 0,
    [theme.breakpoints.down(465)]: {
      padding: 16,
    },
  },
  arrowMargins: {
    marginBottom: 18,
    marginTop: 20,
  },
  iconsWrap: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
    flexWrap: 'wrap',
    width: '100%',
    overflowY: 'scroll',
    msOverflowStyle: 'none',
    overflow: '-moz-scrollbars-none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  pointer: {
    cursor: 'pointer',
  },
  msgWrap: {
    height: '85%',
    width: '100%',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
});

interface IProps extends WithStyles<typeof styles> {
  tables: Array<ITable> | void;
  isMobile?: boolean; // Для fullscreen режима (ширина < 600px или высота < 600px);
  handleSelectTable: (table: ITable) => void;
  activeTables: TActiveTables;
  isMergeTables: boolean;
  schema: TSectionAdaptSchema | void;
  viewId: IViewType;
  isSchemasEnabled: boolean;
  persons: number;
  alreadySelectedTables: Array<number>;
}

const RestaurantView: React.FC<IProps> = ({
  classes,
  tables,
  handleSelectTable,
  activeTables,
  isMergeTables,
  schema,
  viewId,
  isSchemasEnabled,
  persons,
  alreadySelectedTables,
  isMobile,
}) => {
  const tableCont = React.createRef<any>();
  const [initialized, onInit] = useState(false);

  useEffect(() => {
    const tablesReady = tables && tables.length > 0;
    if (viewId === 'scheme') {
      if (schema && !initialized) {
        onInit(true);
      }
    } else if (tablesReady && !initialized) {
      onInit(true);
    }
  }, [viewId, schema, tables, initialized]);

  const initMessage = (
    <div className={classes.msgWrap}>
      <Typography>
        Укажите кол-во гостей, дату и время,
      </Typography>
      <Typography>
        чтобы увидеть доступные столики
      </Typography>
    </div>
  );

  const noFreeTables = (
    <div className={classes.msgWrap}>
      {
        !isMergeTables ? (
          <span>
            <Typography>
              Нет доступных столов, Попробуйте
            </Typography>
            <Typography>
              включить опцию "Объединить столы"
            </Typography>
          </span>
        ) : (
          <Typography>
            Нет доступных столов
          </Typography>
        )
      }
    </div>
  );

  if (viewId === 'scheme') {
    const disabledSchema = (
      <div className={classes.msgWrap}>
        <Typography>
          {locales.t('restaurantSchema.note.schemasDisabled')}
        </Typography>
      </div>
    );
    if (!isSchemasEnabled && initialized) {
      return (
        <div className={classes.wrap}>
          {disabledSchema}
        </div>
      );
    }

    const emptySchema = (
      <div className={classes.msgWrap}>
        <Typography>
          {locales.t('restaurantSchema.note.schemaDontExists')}
        </Typography>
      </div>
    );

    // Показываем схему, если она существует, даже если tables еще не загружены
    // Это важно для модального окна редактирования, где freeTables могут загружаться асинхронно
    if (!schema) {
      return (
        <div
          className={classes.wrap}
          style={isMobile ? { height: '100%', minHeight: 0 } : undefined}
        >
          {emptySchema}
        </div>
      );
    }

    // Если есть схема, показываем её даже если tables пустой или undefined
    // RestaurantSchema может работать с пустым массивом tables
    const tablesSchema = (
      <RestaurantSchema
        activeTables={activeTables}
        data={schema}
        tables={tables || []}
        handleSelect={handleSelectTable}
        persons={persons}
        customWidthOffset
        // @todo Реализовать на схеме
        // alreadySelectedTables={alreadySelectedTables}
      />
    );

    return (
      <div
        className={classes.wrap}
        style={isMobile ? { height: '100%', minHeight: 0 } : undefined}
      >
        {tablesSchema}
      </div>
    );
  }

  const scrollToElement = (type: string): void => {
    if (type === 'top') {
      tableCont.current.scrollTop -= 80;
    } else {
      tableCont.current.scrollTop += 80;
    }
  };

  const tablesGrid = (
    tables && tables.length > 0 ? (
        <Grid
          container
          spacing={isMobile ? 2 : 3}
          alignItems="stretch"
        >
          {tables.map(
            table => (
              <Grid
                item
                xs={isMobile ? 4 : 6}
                sm={3}
                xl={2}
                key={table.id}
              >
                <RestaurantIcon
                  table={table}
                  handleSelect={handleSelectTable}
                  active={!!activeTables[table.id]}
                  // Локальная блокировка отключена, так как текущая реализация
                  //   не покрывает 100% случаев. Не удаляем, так как планируем
                  //   вернуться к этому позже
                  // disabled={alreadySelectedTables.indexOf(table.id) !== -1}
                />
              </Grid>
            ),
          )}
        </Grid>
      )
      : noFreeTables
  );

  return (
    <div
      className={classes.wrap}
      style={isMobile ? { height: '100%' } : undefined}
    >
      {/* Скрываем стрелки на мобильных и планшетах */}
      {!isMobile && (
        <ArrowTop
          className={classes.pointer}
          onClick={() => scrollToElement('top')}
        />
      )}
      <div
        ref={tableCont}
        className={classNames(classes.iconsWrap, {
          [classes.arrowMargins]: !isMobile, // Убираем отступы на мобильных, когда нет стрелок
        })}
      >
        {tables === undefined ? initMessage : tablesGrid}
      </div>
      {!isMobile && (
        <ArrowBottom
          className={classes.pointer}
          onClick={() => scrollToElement('down')}
        />
      )}
    </div>
  );
};

export default withStyles(styles)(RestaurantView);
